/**
 * Form Input Helper Utilities
 * 
 * Provides utility functions for handling numeric form inputs cleanly,
 * particularly to prevent leading zeros in number fields.
 */

/**
 * Returns the value for display in a numeric TextField.
 * - Returns empty string for 0, "0", null, undefined, or empty string
 * - Returns the value as-is for any other value
 * 
 * This prevents the "leading zero" issue where typing in a field initialized to "0"
 * results in values like "05" instead of "5".
 * 
 * @param {string|number} value - The raw value from state
 * @returns {string} - The value to display in the input
 */
export function numericDisplayValue(value) {
    if (value === null || value === undefined || value === '') return '';
    if (value === 0 || value === '0') return '';
    return String(value);
}

/**
 * Parses a numeric input value, stripping leading zeros.
 * - Preserves empty string as empty string
 * - Strips leading zeros from numeric strings
 * - Returns parsed string for state storage
 * 
 * @param {string} inputValue - The raw value from the input event
 * @returns {string} - The cleaned value for state
 */
export function parseNumericInput(inputValue) {
    if (inputValue === '' || inputValue === null || inputValue === undefined) {
        return '';
    }

    // Handle negative numbers
    const isNegative = inputValue.startsWith('-');
    let val = isNegative ? inputValue.slice(1) : inputValue;

    // Handle decimal numbers
    const hasDecimal = val.includes('.');

    if (hasDecimal) {
        // For decimals, just strip leading zeros before the decimal point
        const [intPart, decPart] = val.split('.');
        const cleanInt = intPart.replace(/^0+/, '') || '0';
        return (isNegative ? '-' : '') + cleanInt + '.' + decPart;
    } else {
        // For whole numbers, strip all leading zeros unless it's just "0"
        const cleanVal = val.replace(/^0+/, '') || '0';
        // If input was just zeros, and we want to allow empty field
        if (cleanVal === '0' && val !== '0') {
            return '';
        }
        return (isNegative ? '-' : '') + cleanVal;
    }
}

/**
 * For use with onChange handlers - strips leading zeros as user types.
 * 
 * Usage:
 * onChange={(e) => setValue(handleNumericChange(e.target.value))}
 * 
 * @param {string} value - The raw input value
 * @returns {string} - The cleaned value
 */
export function handleNumericChange(value) {
    // Allow empty input
    if (value === '') return '';

    // For a field that user is typing into, preserve the value if it's valid
    // but strip unnecessary leading zeros
    // e.g., "007" -> "7", "0.5" -> "0.5", "00" -> "0"

    const hasDecimal = value.includes('.');
    const isNegative = value.startsWith('-');

    let val = isNegative ? value.slice(1) : value;

    if (hasDecimal) {
        const [intPart, decPart] = val.split('.');
        // Preserve "0." as a valid intermediate state
        if (intPart === '' || intPart === '0' || /^0+$/.test(intPart)) {
            return (isNegative ? '-' : '') + '0.' + (decPart || '');
        }
        const cleanInt = intPart.replace(/^0+/, '');
        return (isNegative ? '-' : '') + cleanInt + '.' + (decPart || '');
    } else {
        // Strip leading zeros unless it's exactly "0"
        if (/^0+$/.test(val)) {
            return (isNegative ? '-' : '') + '0';
        }
        const cleanVal = val.replace(/^0+/, '');
        return (isNegative ? '-' : '') + (cleanVal || '0');
    }
}

export default {
    numericDisplayValue,
    parseNumericInput,
    handleNumericChange,
};
